import React, { useState, useEffect, useRef } from 'react';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  altText?: string;
  alt?: string;
}

const ImageModal: React.FC<ImageModalProps> = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  altText, 
  alt 
}) => {
  const [scale, setScale] = useState(1);
  const [naturalDimensions, setNaturalDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Modal açıldığında ve resim yüklendiğinde ölçeği sıfırla
  useEffect(() => {
    if (isOpen && imageUrl) {
      setScale(1); // Ölçeği sıfırla
      
      // Resimin gerçek boyutu için bir Image objesi kullan
      const img = new Image();
      img.onload = () => {
        setNaturalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
        console.log(`Resim gerçek boyutu: ${img.naturalWidth}x${img.naturalHeight}`);
      };
      img.src = imageUrl;
    }
  }, [isOpen, imageUrl]);
  
  if (!isOpen) return null;
  
  const imageAltText = alt || altText || 'Görsel';
  
  const handleZoomIn = () => {
    setScale(prev => prev + 0.2);
  };
  
  const handleZoomOut = () => {
    setScale(prev => prev > 0.4 ? prev - 0.2 : prev);
  };
  
  const handleReset = () => {
    setScale(1);
  };
  
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Başlangıçta (scale=1 iken) resmi ekrana sığdır, zoom yapıldığında taşmasına izin ver
  const shouldConstrain = scale <= 1;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg w-[90vw] h-[90vh] relative flex items-center justify-center"
        ref={containerRef}
      >
        {/* Kontrol butonları */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <button 
            onClick={handleZoomIn}
            className="p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-opacity shadow-lg"
            aria-label="Büyüt"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button 
            onClick={handleZoomOut}
            className="p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-opacity shadow-lg"
            aria-label="Küçült"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button 
            onClick={handleReset}
            className="p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-opacity shadow-lg"
            aria-label="Orijinal Boyut"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button 
            onClick={onClose}
            className="p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-opacity shadow-lg"
            aria-label="Kapat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Resim container - overflow auto ile kaydırma çubuklarını aktif et */}
        <div className="overflow-auto w-full h-full p-4">
          <div className="relative flex items-center justify-center min-h-full min-w-full">
            <img
              src={imageUrl}
              alt={imageAltText}
              className="transition-transform duration-300 ease-in-out object-contain max-h-[80vh] max-w-[80vw]"
              style={{ 
                transform: `scale(${scale})`,
                transformOrigin: 'center center',
              }}
              onLoad={(e) => {
                const img = e.target as HTMLImageElement;
                setNaturalDimensions({
                  width: img.naturalWidth,
                  height: img.naturalHeight
                });
              }}
            />
            
            {/* Resim boyutu bilgisi */}
            {naturalDimensions.width > 0 && (
              <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                {naturalDimensions.width} x {naturalDimensions.height} | Zoom: {(scale * 100).toFixed(0)}%
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
